"use strict";
/**
 * Kalshi series taxonomy: a series ticker is a sport stem plus a market-type
 * suffix (`KXMLB` + `F5SPREAD`, `KXNBA` + `1QWINNER`). Both fall out of the
 * string with no lookup, which is why Kalshi categories backfill and PM-US's
 * can't (ai/PNL_TOTALS_PLAN.md §6).
 *
 * Shared: the client renders these as selectors, the server stamps them onto
 * order_events. One registry, so the two can't drift.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.KALSHI_SPORT_STEMS = exports.toLeaguePrefix = exports.LEAGUES = void 0;
exports.parseKalshiSeries = parseKalshiSeries;
exports.kalshiCoarseType = kalshiCoarseType;
exports.kalshiCategories = kalshiCategories;
/** League abbreviation → its moneyline (`…GAME`) series ticker. */
exports.LEAGUES = {
    /** National Basketball Association */
    NBA: "KXNBAGAME",
    /** National Football League */
    NFL: "KXNFLGAME",
    /** Major League Baseball */
    MLB: "KXMLBGAME",
    /** National Hockey League */
    NHL: "KXNHLGAME",
    /** College Football (NCAA FBS) */
    CFB: "KXNCAAFGAME",
    /** College Basketball – Men (NCAA) */
    CBB: "KXNCAAMBGAME",
    /** College Basketball – Women (NCAA) */
    WCBB: "KXNCAAWBGAME",
    /** Major League Soccer */
    MLS: "KXMLSGAME",
    /** College Baseball (NCAA) */
    CBSBL: "KXNCAABBGAME",
    /** College Hockey (NCAA) */
    CHKY: "KXNCAAHOCKEYGAME",
    /** WNBA */
    WNBA: "KXWNBAGAME",
};
/** `KXMLBGAME` → `KXMLB`, keeping the stem list derived from {@link LEAGUES}. */
const toLeaguePrefix = (ticker) => ticker.replace(/GAME$/, "");
exports.toLeaguePrefix = toLeaguePrefix;
/**
 * Sport stems, **longest first** — load-bearing: `KXNCAAF`, `KXNCAABB` and
 * `KXNCAAHOCKEY` share a prefix, so a shortest-match scan would file college
 * baseball under college football.
 */
exports.KALSHI_SPORT_STEMS = Object.values(exports.LEAGUES)
    .map(exports.toLeaguePrefix)
    .sort((a, b) => b.length - a.length);
/**
 * Split a series ticker into sport stem + market type. An unrecognised series
 * yields the whole string as the stem and no market type, rather than a guessed
 * split that would file it under the wrong sport.
 */
function parseKalshiSeries(series) {
    for (const stem of exports.KALSHI_SPORT_STEMS) {
        if (series.startsWith(stem)) {
            return { sportStem: stem, marketType: series.slice(stem.length) };
        }
    }
    return { sportStem: series, marketType: "" };
}
/**
 * Coarse bucket for a market-type suffix; null when we can't tell, since
 * "unknown" must stay distinct from a confident "props".
 *
 * Winner suffixes are irregular (bare `GAME`/`F5`, but `…WINNER` for halves and
 * quarters) while spread/total markets always end in `SPREAD`/`TOTAL` — testing
 * endings first means new periods need no change here.
 */
function kalshiCoarseType(marketType) {
    if (!marketType)
        return null;
    if (marketType.endsWith("SPREAD"))
        return "spreads";
    if (marketType.endsWith("TOTAL"))
        return "totals";
    if (marketType === "GAME" || marketType === "F5")
        return "moneyline";
    if (marketType.endsWith("WINNER"))
        return "moneyline";
    return "props";
}
/**
 * Reporting categories for a series ticker: `category` is the fine bucket (the
 * suffix, lowercased), `coarse` the cross-venue one. Both null if unrecognised.
 */
function kalshiCategories(series) {
    const { marketType } = parseKalshiSeries(series);
    return {
        category: marketType ? marketType.toLowerCase() : null,
        coarse: kalshiCoarseType(marketType),
    };
}
